import React, { useEffect, useMemo } from 'react'
import { Route, useHistory, useLocation } from 'react-router-dom'
import { useEffectOnce } from 'react-use'
import isEmpty from 'lodash/isEmpty'
import values from 'lodash/values'
import { Box, Grid, Typography, Skeleton } from 'transfix-ui/core'
import { getApiUrl } from 'utils/api/apiHelpers'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useLaneFiltersContext } from 'apps/fleet-planner/context/LaneFiltersContext'
import { useMembershipContext } from 'apps/fleet-planner/context/MembershipContext'
import { DedicatedLanesBidModal } from 'apps/fleet-planner/pages/dedicated-lanes/DedicatedLanesBidModal'
import { getDedicatedLanesSearchFiltersFromSavedSeaches } from 'apps/fleet-planner/utils/dedicatedLanesHelpers'
import {
  DedicatedLanesFilters,
  useGetDedicatedLanes,
} from 'apps/marketplace/apiTypes/dedicatedLanes'
import { Table, TableBody, TableCell, TableRow, Card } from 'transfix-ui'
import { LaneOffersTable } from './DedicatedLanesOffersTable'

export const DedicatedLanesBookLoadsResults = () => {
  const { search } = useLocation()
  const { activeUserMotorCarriers, getUserMotorCarriers } = useMembershipContext()
  const { isMobile } = useBreakpointContext()
  const history = useHistory()

  const {
    data: lanesData,
    loading: isLoading,
    refetch: refetchDedicatedLanes,
  } = useGetDedicatedLanes({
    lazy: true,
    queryParamStringifyOptions: {
      encodeValuesOnly: true,
      arrayFormat: 'brackets',
    },
    base: getApiUrl('MARKETPLACE'),
  })

  const { hasStartedSearch, params: laneParams } = useLaneFiltersContext()
  const { dedicatedLanes = [] } = lanesData || {}

  const closeOfferDetailsHandler = () => {
    history.push(`/book${search}`)
  }

  const { filters: laneFilters } = laneParams || {}

  // Check if any key in params.filters is truthy.
  const hasActiveFilters = !values(laneFilters).every(isEmpty) && hasStartedSearch
  const showLaneOffers = dedicatedLanes.length > 0 && !isLoading && hasStartedSearch

  const hasActiveCarriers = useMemo(() => activeUserMotorCarriers.length > 0, [
    activeUserMotorCarriers,
  ])

  // Fetch users motor carriers once
  useEffectOnce(() => {
    getUserMotorCarriers()
  })

  // Fetch dedicated lanes only when a searched request has been submitted.
  useEffect(() => {
    if (hasActiveFilters) {
      const getDedicatedLanes = async (filters: DedicatedLanesFilters) => {
        await refetchDedicatedLanes({ queryParams: { filters } })
      }
      const dedicatedLaneFilters: DedicatedLanesFilters =
        getDedicatedLanesSearchFiltersFromSavedSeaches(laneParams) || {}
      getDedicatedLanes({ ...dedicatedLaneFilters })
    }
  }, [hasActiveFilters, laneParams, refetchDedicatedLanes])

  return hasActiveCarriers ? (
    <Box>
      {hasActiveFilters && (
        <Box>
          {/* Loading state. */}
          {isLoading && (
            <Box>
              <Box paddingLeft={3}>
                <Typography variant='subtitle1'>Dedicated lanes</Typography>
                <Typography variant='body2'>
                  Place a bid to get priority access to loads as a dedicated carrier.
                </Typography>
              </Box>
              <Card className='pa0 ma4'>
                <Box margin={2}>
                  <Table>
                    <TableBody>
                      {isMobile ? (
                        /** Mobile view */
                        <>
                          <TableRow>
                            <TableCell>
                              <Grid container>
                                <Grid item xs={12} className='pb4'>
                                  <Skeleton variant='rectangular' height='55px' width='100%' />
                                </Grid>
                                <Grid item xs={12}>
                                  <Skeleton variant='rectangular' height='55px' width='100%' />
                                </Grid>
                              </Grid>
                            </TableCell>
                          </TableRow>
                        </>
                      ) : (
                        /** Desktop view */
                        <TableRow>
                          <TableCell>
                            <Skeleton variant='rectangular' height='22px' width='110px' />
                          </TableCell>
                          <TableCell>
                            <Skeleton variant='rectangular' height='22px' width='260px' />
                          </TableCell>
                          <TableCell>
                            <Skeleton variant='rectangular' height='22px' width='140px' />
                          </TableCell>
                          <TableCell>
                            <Skeleton variant='rectangular' height='22px' width='140px' />
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                    <></>
                  </Table>
                </Box>
              </Card>
            </Box>
          )}
          {showLaneOffers && (
            <Box paddingBottom={2}>
              <LaneOffersTable dedicatedLanes={dedicatedLanes} />
            </Box>
          )}
        </Box>
      )}
      <Route path='/book/:laneId/lane-details'>
        <DedicatedLanesBidModal onClose={closeOfferDetailsHandler} />
      </Route>
    </Box>
  ) : (
    <></>
  )
}
